import { useEffect, useState } from "react";
import { CenterTacticalData, GenerationType, TacticalInterval } from "../../types";
import RadarIcon from '@mui/icons-material/Radar';
import { SlEnergy } from "react-icons/sl";
import { LuGauge } from "react-icons/lu";
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import KPIComponent, { KPIComponentProps } from "./KPIComponent";
import EmptyStateNotFound from "../../../clients/components/EmptyState";
import { Screen } from "../../../../main/components/screen/Screen";
import { Box, Grid, Typography, ToggleButton, ToggleButtonGroup } from "@mui/material";
import Loading from "../../../../main/components/Loading";
import { GoPulse } from "react-icons/go";
import { Accordion } from "../../../../main/components/accordion/Accordion";
import MovingIcon from '@mui/icons-material/Moving';
import { formatNumberPercent } from "../../../dashboard/helpers/format-number";
import PerformancePerAssetTable from "./PerformancePerAssetTable";
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import BaseButton from "../../../../main/components/BaseButton";
import TacticalFiltersModal from "./TacticalFiltersModal";
import { operationsApi } from "../../../../services/api";
import AvailabilityAverageTacticalChart from "./AvailabilityAverageTacticalChart";
import SpecificProductivityTacticalChart from "./SpecificProductivityTacticalChart";
import ImpactoNaMetaTactical from "./ImpactoNaMetaTactical";
import dayjs, { Dayjs } from "dayjs";
import AnaliseDisponibilidadeTactical from "./AnaliseDisponibilidadeTactical";
import AnaliseCausaRaiz from "./AnaliseCausaRaiz";
import AlertasCriticosDesvios from "./AlertasCriticosDesvios";
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useClientClusterUsinaFilterContext } from "../../../../main/context/ClientClusterUsinaFilterContextProvider";
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

export default function TacticalScreen() {
    const {
        selectedClientFilter,
        selectedCluster,
        selectedUsinaFilter,
        availableUsinas,
    } = useClientClusterUsinaFilterContext();

    const getLastMonday = () => {
        const today = dayjs();
        const dayOfWeek = today.day();

        if (dayOfWeek === 1) {
            return today;
        } else if (dayOfWeek === 0) {
            // Se for domingo, volta 6 dias para pegar a segunda anterior
            return today.subtract(6, 'day');
        } else {
            return today.subtract(dayOfWeek - 1, 'day');
        }
    };

    const [centerTacticalData, setCenterTacticalData] = useState<CenterTacticalData | null>(null);
    const [loading, setLoading] = useState(false);
    const [chartViewMode, setChartViewMode] = useState<"cluster" | "powerPlant">("cluster");

    const [selectedAtivo, setSelectedAtivo] = useState<GenerationType | undefined>(undefined);
    const [selectedInterval, setSelectedInterval] = useState<TacticalInterval>(TacticalInterval.Daily);
    const [showFiltersModal, setShowFiltersModal] = useState(false);
    const [selectedStartDate, setSelectedStartDate] = useState<Dayjs | null>(dayjs().subtract(7, "days"));
    const [selectedEndDate, setSelectedEndDate] = useState<Dayjs | null>(dayjs());
    const [selectedWeeklyDate, setSelectedWeeklyDate] = useState<Dayjs | null>(getLastMonday());
    const [selectedMonthlyDate, setSelectedMonthlyDate] = useState<Dayjs | null>(dayjs());

    const mount = async () => {
        try {
            setLoading(true);

            let startDateParam: string | undefined;
            let endDateParam: string | undefined;
            let weeklyDateParam: string | undefined;
            let monthParam: number | undefined;
            let yearParam: number | undefined;

            if (selectedInterval === TacticalInterval.Custom) {
                startDateParam = selectedStartDate?.format("YYYY-MM-DD");
                endDateParam = selectedEndDate?.format("YYYY-MM-DD");
            } else if (selectedInterval === TacticalInterval.Weekly) {
                weeklyDateParam = selectedWeeklyDate?.format("YYYY-MM-DD");
            } else if (selectedInterval === TacticalInterval.Monthly) {
                monthParam = selectedMonthlyDate ? selectedMonthlyDate?.month() + 1 : 0;
                yearParam = selectedMonthlyDate?.year();
            }

            const currentUsina = availableUsinas.find(
                (usina) => usina.usinaIds.id === selectedUsinaFilter?.id
            );

            const response = await operationsApi.getCenterTactical(
                selectedInterval,
                selectedClientFilter?.id,
                selectedCluster?.id,
                currentUsina?.usinaIds.ropInputId,
                selectedAtivo,
                startDateParam,
                endDateParam,
                weeklyDateParam,
                monthParam,
                yearParam,
            );

            setCenterTacticalData(response);
        } catch (error) {
            throw error;
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        mount();
    }, [selectedInterval, selectedAtivo, selectedStartDate, selectedEndDate, selectedWeeklyDate, selectedMonthlyDate, selectedClientFilter, selectedCluster, selectedUsinaFilter]);

    const averagePower = centerTacticalData?.averagePowerCard.overallAverage ?? 0;
    const generatedEnergy = centerTacticalData?.generatedEnergyCard.overallTotal ?? 0;
    const manageableLoss = centerTacticalData?.manageableLossCard.overallTotal ?? 0;
    const generationEffectivenesValue = centerTacticalData?.generationEffectivenessCard.overallAverage ? centerTacticalData.generationEffectivenessCard.overallAverage / 100 : 0;

    const calculateAverageExpectedPR = (): number => {
        const clusterPerformances = centerTacticalData?.performanceByAssetCard?.clusterPerformances ?? [];
        if (clusterPerformances.length === 0) return 0;

        let totalExpectedPR = 0;
        let count = 0;

        clusterPerformances.forEach(cluster => {
            if (cluster.expectedPerformanceRatio > 0) {
                totalExpectedPR += cluster.expectedPerformanceRatio;
                count++;
            }
        });

        return count > 0 ? totalExpectedPR / count : 0;
    };

    const averageExpectedPR = calculateAverageExpectedPR();
    const currentPR = centerTacticalData?.performanceRatioCard.overallAverage ?? 0;
    const prDeviationPercent = averageExpectedPR > 0 ? ((currentPR - averageExpectedPR) / averageExpectedPR) * 100 : 0;

    const CardsData: KPIComponentProps[] = [
        {
            icon: <LuGauge style={{ color: "#528AD1" }} />,
            sufixx: "%",
            title: "Performance Ratio",
            value: formatNumberPercent(currentPR, 2, false, false) || "0%",
            gridSize: { xs: 6, md: 2 },
            children:
                <Box sx={{ display: "flex", flexDirection: "row" }}>
                    {prDeviationPercent > 0 ? <MovingIcon style={{ color: "#47D27B", fontSize: "18px", marginRight: "8px" }} /> : <TrendingDownIcon style={{ color: "#EF4444", fontSize: "18px", marginRight: "8px" }} />}
                    <Typography sx={{ fontSize: "13px", fontWeight: "400", color: prDeviationPercent >= 0 ? "#47D27B" : "#EF4444" }}>
                        {prDeviationPercent >= 0 ? "+" : ""}{prDeviationPercent.toFixed(1)}%
                    </Typography>
                </Box>
        },
        {
            icon: <RadarIcon sx={{ color: "#B980F3" }} />,
            sufixx: "MW",
            title: "Potência Média",
            gridSize: { xs: 6, md: 2 },
            value: averagePower.toFixed(2) || "0%",
            children:
                <Box sx={{ display: "flex", flexDirection: "row" }}>
                    <Typography sx={{ fontSize: "13px", fontWeight: "400", color: "#47D27B" }}>Injetada na rede</Typography>
                </Box>
        },
        {
            icon: <SlEnergy style={{ color: "#FBBF24" }} />,
            sufixx: "",
            title: "Energia Gerada",
            value: generatedEnergy.toFixed(2) || "0.00",
            gridSize: { xs: 6, md: 2 },
            children:
                <Box sx={{ display: "flex", flexDirection: "row" }}>
                    <Typography sx={{ fontSize: "13px", fontWeight: "400", color: "#F85F56" }}>MWh no período</Typography>
                </Box>
        },
        {
            icon: <GoPulse style={{ color: "#2CCBB8" }} />,
            sufixx: "%",
            title: "Eficácia de Geração",
            value: formatNumberPercent(generationEffectivenesValue, 2, false) || "0%",
            gridSize: { xs: 6, md: 2 },
            children:
                <Box sx={{ display: "flex", flexDirection: "row" }}>
                    <Typography sx={{ fontSize: "13px", fontWeight: "400", color: "#47D27B" }}>Eficiência operacional</Typography>
                </Box>
        },
        {
            icon: <TrendingDownIcon sx={{ color: "#FB773B" }} />,
            sufixx: "",
            title: "Perdas Gerenciáveis",
            value: manageableLoss.toFixed(2) || "0.00",
            gridSize: { xs: 6, md: 2 },
            children:
                <Box sx={{ display: "flex", flexDirection: "row" }}>
                    <Typography sx={{ fontSize: "13px", fontWeight: "400" }}>MWh no período</Typography>
                </Box>
        },
        {
            icon: <AccessTimeOutlinedIcon sx={{ color: "#47D27B" }} />,
            sufixx: "%",
            title: "Disponibilidade",
            value: formatNumberPercent(centerTacticalData?.availabilityCard.overallAverage ?? 0, 2, false, false) || "0%",
            gridSize: { xs: 6, md: 2 },
            children:
                <Box sx={{ display: "flex", flexDirection: "row" }}>
                    <Typography sx={{ fontSize: "13px", fontWeight: "400", color: "#47D27B" }}>Média dos inversores</Typography>
                </Box>
        },

    ]

    const [showPerformancePerAssetTable, setShowPerformancePerAssetTable] = useState(true);
    const [showAlertasCriticosDesvios, setShowAlertasCriticosDesvios] = useState(true);

    return (<>
        {loading ? (
            <>
                <Screen.Container>
                    <Loading />
                </Screen.Container>
            </>
        ) : true ? (
            <>
                <Screen.Container sx={{ paddingLeft: "10px", paddingRight: "10px" }} >
                    <Grid
                        container
                        paddingX={{
                            xs: "5px",
                            sm: "30px",
                        }}
                        sx={{ marginY: "14px", columnGap: "12px", flexDirection: "row", display: "flex", justifyContent: "flex-end" }}
                    >
                        <BaseButton sx={{ width: "auto", height: "35px" }} startIcon={<FilterAltIcon style={{ fontSize: "20px" }} />} title="Filtros" onClick={() => setShowFiltersModal(true)} />
                    </Grid>

                    <Grid container sx={{ paddingLeft: "6px", paddingRight: "2px", marginY: "14px", rowGap: "12px" }}>
                        {CardsData.map((item, index) => <KPIComponent key={index} {...item} />)}
                    </Grid>

                    <Grid container >
                        <Grid item xs={12} sx={{ display: "flex", flexDirection: "column" }}>
                            <Accordion.Root sx={{ marginTop: "15px", marginBottom: "0px" }}>
                                <Accordion.Header
                                    toggleDetails={() => setShowPerformancePerAssetTable(!showPerformancePerAssetTable)}
                                    showDetails={showPerformancePerAssetTable}
                                    title={``}
                                    typographySx={{
                                        display: "none"
                                    }}
                                    children={
                                        <Box sx={{ display: "flex", flexDirection: "row" }}>
                                            <Box sx={{ display: "flex", marginX: "16px", alignItems: "center" }}>
                                                <SlEnergy style={{ color: "#FBBF24", fontSize: "20px" }} />
                                            </Box>
                                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                                                <Typography sx={{ fontSize: "18px", fontWeight: "500" }}>Performance por Ativo</Typography>
                                                <Typography sx={{ fontSize: "13px", fontWeight: "300" }}>Geração, PR, disponibilidade e comparação com metas</Typography>
                                            </Box>
                                        </Box>
                                    }
                                />
                                <Accordion.Content
                                    showDetails={showPerformancePerAssetTable}
                                    sx={{ maxHeight: "600px", overflow: "auto" }}
                                >

                                    <PerformancePerAssetTable
                                        clusters={centerTacticalData?.performanceByAssetCard?.clusterPerformances ?? []}
                                        loading={loading}
                                        selectedClusterName={selectedCluster?.name}
                                        inverterAvailabilityExpectedValue={centerTacticalData?.availabilityAnalysisCard.overallAverage.inverterExpectedValue}
                                    />
                                </Accordion.Content>
                            </Accordion.Root>
                        </Grid>

                    </Grid>

                    <Grid container spacing={1.5}>
                        <Grid item xs={12} marginY={"12px"}>
                            {centerTacticalData && (
                                <ImpactoNaMetaTactical
                                    data={centerTacticalData.targetImpactCard}
                                    performanceByAsset={centerTacticalData.performanceByAssetCard}
                                    interval={selectedInterval}
                                    criticalDeviationsTotal={centerTacticalData.manageableLossCard?.criticalDeviationsOverallTotal}
                                    selectedMonth={selectedMonthlyDate ? (selectedMonthlyDate?.month() + 1) : 0}
                                    selectedYear={selectedMonthlyDate?.year()}
                                    customStartDate={selectedStartDate?.format("YYYY-MM-DD")}
                                    customEndDate={selectedEndDate?.format("YYYY-MM-DD")}
                                />
                            )}
                        </Grid>
                    </Grid>

                    <Grid container spacing={1.5} marginBottom={"12px"}>
                        {centerTacticalData?.rootCauseAnalysisCard && (
                            <AnaliseCausaRaiz
                                data={centerTacticalData.rootCauseAnalysisCard}
                                viewMode={chartViewMode}
                            />
                        )}
                    </Grid>

                    <Grid container sx={{ paddingLeft: "6px", paddingRight: "2px", marginY: "14px", rowGap: "12px" }}>
                        <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>

                            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginBottom: "16px" }}>
                                <ToggleButtonGroup
                                    value={chartViewMode}
                                    exclusive
                                    onChange={(_, newMode) => {
                                        if (newMode !== null) {
                                            setChartViewMode(newMode);
                                        }
                                    }}
                                    size="small"
                                    sx={{
                                        backgroundColor: "transparent",
                                        "& .MuiToggleButton-root": {
                                            border: `1px solid`,
                                            borderColor: "divider",
                                            color: "inherit",
                                            padding: "6px 12px",
                                            fontSize: "12px",
                                            "&.Mui-selected": {
                                                backgroundColor: "rgba(128, 128, 128, 0.12)",
                                                color: "inherit",
                                            },
                                        },
                                    }}
                                >
                                    <ToggleButton value="cluster" aria-label="Cluster">
                                        Por Cluster
                                    </ToggleButton>
                                    <ToggleButton value="powerPlant" aria-label="Power Plant">
                                        Por Usina
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                            <Grid container spacing={1.5}>
                                <Grid item xs={12} sm={6}>
                                    {centerTacticalData && (
                                        <AvailabilityAverageTacticalChart
                                            data={centerTacticalData.availabilityCard}
                                            viewMode={chartViewMode}
                                        />
                                    )}
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    {centerTacticalData && (
                                        <SpecificProductivityTacticalChart
                                            data={centerTacticalData.productivityCard}
                                            viewMode={chartViewMode}
                                        />
                                    )}
                                </Grid>
                            </Grid>
                            <Grid container >
                                <Grid item xs={12} sx={{ display: "flex", flexDirection: "column" }}>
                                    <Accordion.Root sx={{ marginTop: "15px", marginBottom: "0px" }}>
                                        <Accordion.Header
                                            toggleDetails={() => setShowAlertasCriticosDesvios(!showAlertasCriticosDesvios)}
                                            showDetails={showAlertasCriticosDesvios}
                                            title={``}
                                            typographySx={{
                                                display: "none"
                                            }}
                                            children={
                                                <Box sx={{ display: "flex", flexDirection: "row" }}>
                                                    <Box sx={{ display: "flex", marginX: "16px", alignItems: "center" }}>
                                                        <WarningAmberIcon sx={{ color: "#F85F56", fontSize: "28px" }} />
                                                    </Box>
                                                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                                                        <Typography sx={{ fontSize: "18px", fontWeight: "500" }}>Alertas Críticos e Desvios</Typography>
                                                        {/* TODO: <Typography sx={{ fontSize: "13px", fontWeight: "300" }}>2 críticos, 4 não resolvidos</Typography> */}
                                                    </Box>
                                                </Box>
                                            }
                                        />
                                        <Accordion.Content
                                            showDetails={showAlertasCriticosDesvios}
                                        >
                                            {centerTacticalData && (
                                                <AlertasCriticosDesvios
                                                    data={centerTacticalData.criticalAlertsAndDeviationsCard}
                                                    clusterId={selectedCluster?.id}
                                                    usinaId={availableUsinas.find(u => u.usinaIds.id === selectedUsinaFilter?.id)?.usinaIds.ropInputId}
                                                />
                                            )}
                                        </Accordion.Content>
                                    </Accordion.Root>
                                </Grid>

                            </Grid>
                            <Grid container spacing={1.5} marginY={"12px"}>
                                {centerTacticalData &&
                                    <AnaliseDisponibilidadeTactical data={centerTacticalData?.availabilityAnalysisCard} equipmentsUnavailability={centerTacticalData?.equipmentsUnavailabilityCard} />
                                }
                            </Grid>

                        </Box>
                    </Grid>
                </Screen.Container>
                <TacticalFiltersModal
                    open={showFiltersModal}
                    setOpen={setShowFiltersModal}
                    selectedAtivo={selectedAtivo}
                    setSelectedAtivo={setSelectedAtivo}
                    selectedInterval={selectedInterval}
                    setSelectedInterval={setSelectedInterval}
                    selectedStartDate={selectedStartDate}
                    setSelectedStartDate={setSelectedStartDate}
                    selectedEndDate={selectedEndDate}
                    setSelectedEndDate={setSelectedEndDate}
                    selectedWeeklyDate={selectedWeeklyDate}
                    setSelectedWeeklyDate={setSelectedWeeklyDate}
                    selectedMonthlyDate={selectedMonthlyDate}
                    setSelectedMonthlyDate={setSelectedMonthlyDate}
                />
            </>
        ) : (
            <Screen.Container>
                <EmptyStateNotFound title="Nenhum dado encontrado" />
            </Screen.Container>
        )}
    </>
    )
}